/**
 * COMPOUND — industrial-yard realism pass (2026-11-04, owner: "improve each
 * map", "realistic", "get inspired by existing models, don't copy them").
 *
 * The compound is a fenced container yard: two sheet-steel warehouses on the
 * short ends, a maze of ISO containers in the middle, palletised cover and a
 * 5 m perimeter wall. Before this pass the containers were plain painted
 * boxes (no box under 3.2 m gets architectural dressing), the wall top was a
 * knife edge, the warehouses had no doors or services, and nothing on the
 * ground said "working yard". Inspiration (not copies): container-terminal and
 * logistics-park photography plus the usual industrial prop kits —
 * corrugated ISO boxes with corner castings, door locking bars and owner
 * codes; roller shutters with hoods and guide rails; wall packs, downpipes,
 * louvred vents, electrical panels; razor-wire concertina and floodlights on
 * the perimeter; hazard hatching, bay marks and a helipad on the slab.
 *
 * Structure (same as neon_district.ts): `compoundLayout` is PURE (no three.js,
 * no DOM) and returns a flat list of pieces, so
 * `agent/bench-extras-compound.mjs` checks the collision contract against the
 * real shared boxes. `build` turns the list into ONE merged mesh per material
 * — 7 draw calls, built once, no lights, no per-frame work.
 *
 * Contract (see ../mapExtras.ts):
 *  - `skin` pieces sit ON a face of their host collider, ≤ WALL_PROUD proud,
 *    never above its top, never inside another collider, never in a doorway;
 *  - `top` pieces are flush decals (≤ GROUND_LIFT) on a host's top face;
 *  - `ground` pieces are decals ≤ GROUND_LIFT on the floor slabs, under no
 *    collider;
 *  - `atop` pieces (razor wire, floodlights) stand on the perimeter wall top,
 *    inside its footprint — a surface no player stands on.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapBox, MapDef } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { surfaceMaps, worldProjectUV } from "../mapTextures.js";

export const mapIds = ["compound"];

// ---------------------------------------------------------------------------
// contract numbers (the bench quotes these)
export const WALL_PROUD = 0.26;
export const GROUND_LIFT = 0.05;
/** razor wire + floodlights may rise this far above the perimeter wall top */
export const ATOP_MAX = 2.6;
/** goods-in shutter: offset from the yard wall centre (clear of the shopfront) + width */
export const SHUTTER_DZ = -6.75;
export const SHUTTER_W = 3.3;

export type ExtrasMat = "steel" | "dark" | "galv" | "wood" | "paint" | "lamp" | "glass" | "wire";
export type Zone = "skin" | "top" | "ground" | "atop";

export interface ExtrasPiece {
  mat: ExtrasMat;
  zone: Zone;
  /** box = axis-aligned box; rib = box whose outer face is narrowed (a
   *  corrugation crest); quad = one face with normal n; cyl = vertical
   *  cylinder (diameter sx); seg = thin box a→b; coil = concertina wire
   *  helix a→b of radius sy/2 */
  shape: "box" | "rib" | "quad" | "cyl" | "seg" | "coil";
  x: number; y: number; z: number;
  sx: number; sy: number; sz: number;
  nx: number; ny: number; nz: number;
  a?: [number, number, number]; b?: [number, number, number];
  color: number;
  /** atlas region (paint quads) */
  reg?: Region;
  /** host collider index */
  host: number;
  /** weathering gradient: host bottom + height (steel / wood) */
  gy?: number; gh?: number;
}

// deterministic hash in [0,1)
function h01(a: number, b: number, salt: number): number {
  let x = Math.imul((a * 73856093) ^ (b * 19349663) ^ (salt * 83492791), 0x9e3779b1);
  x ^= x >>> 15; x = Math.imul(x, 0x85ebca6b); x ^= x >>> 13;
  return ((x >>> 0) % 100000) / 100000;
}

interface AABB { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number }
const boxAABB = (b: MapBox): AABB => ({
  x0: b.p[0] - b.s[0] / 2, x1: b.p[0] + b.s[0] / 2,
  y0: b.p[1] - b.s[1] / 2, y1: b.p[1] + b.s[1] / 2,
  z0: b.p[2] - b.s[2] / 2, z1: b.p[2] + b.s[2] / 2,
});
const overlaps = (a: AABB, b: AABB, e = 0): boolean =>
  a.x0 < b.x1 - e && a.x1 > b.x0 + e && a.y0 < b.y1 - e && a.y1 > b.y0 + e && a.z0 < b.z1 - e && a.z1 > b.z0 + e;

/** Paint-atlas regions, canvas pixels [x0, y0, x1, y1] on a 1024² canvas. */
export const REGIONS = {
  white: [0, 0, 64, 64], yellow: [64, 0, 128, 64], hazard: [128, 0, 256, 128],
  signA: [0, 128, 512, 256], signB: [0, 256, 512, 384],
  noSmoke: [512, 0, 640, 192], auth: [640, 0, 1024, 192], danger: [512, 192, 1024, 384],
  code0: [0, 384, 512, 432], code1: [0, 432, 512, 480], code2: [0, 480, 512, 528], code3: [0, 528, 512, 576],
  logo0: [0, 576, 512, 704], logo1: [0, 704, 512, 832], logo2: [0, 832, 512, 960],
  forklift: [512, 384, 640, 512], helipad: [512, 512, 1024, 1024],
} as const;
export type Region = keyof typeof REGIONS;

/** One wall face. Tangent axis is the other horizontal one. */
interface Face {
  host: number;
  axis: "x" | "z";
  plane: number;
  sign: 1 | -1;
  t0: number; t1: number;
  y0: number; y1: number;
}

const PAL = {
  hardware: 0x2b2e33, rail: 0x3a3d42, galv: 0xb9bec4, hood: 0x8d939a, signBack: 0x30343a,
  pipe: 0x6d737b, lens: 0xfff1d0, wire: 0x9aa0a6, pole: 0x5d636b, wood: 0xa98a63, woodDark: 0x6e5638,
  shadow: 0x1a1a1a, strap: 0x23262b, glass: 0x9fb4c4,
};

const hexOf = (s: string | undefined, dflt: number): number => {
  if (!s || s[0] !== "#") return dflt;
  const v = parseInt(s.slice(1, 7), 16);
  return Number.isFinite(v) ? v : dflt;
};
/** scale a hex colour channel-wise */
const shade = (hex: number, f: number): number =>
  (Math.min(255, Math.round(((hex >> 16) & 255) * f)) << 16) | (Math.min(255, Math.round(((hex >> 8) & 255) * f)) << 8) | Math.min(255, Math.round((hex & 255) * f));

/** Everything, as data. Pure — no three.js objects are created here. */
export function compoundLayout(map: MapDef, lowSpec: boolean): ExtrasPiece[] {
  const boxes = map.boxes;
  const A = boxes.map(boxAABB);
  const out: ExtrasPiece[] = [];
  // floor slabs (the big ground box + the lot inlay): 1 m thick, top ≈ 0
  const floors = new Set<number>();
  for (let i = 0; i < boxes.length; i++) if (Math.abs(boxes[i].s[1] - 1) < 0.01 && Math.abs(A[i].y1) < 0.05 && boxes[i].s[0] > 20) floors.add(i);
  const floorTop = (x: number, z: number): { host: number; top: number } | null => {
    let best: { host: number; top: number } | null = null;
    for (const i of floors) {
      const a = A[i];
      if (x >= a.x0 && x <= a.x1 && z >= a.z0 && z <= a.z1 && (!best || a.y1 > best.top)) best = { host: i, top: a.y1 };
    }
    return best;
  };

  /** region free of every collider except `skip` + the floors? */
  const clear = (r: AABB, skip: number): boolean => {
    for (let i = 0; i < A.length; i++) {
      if (i === skip || floors.has(i)) continue;
      if (overlaps(r, A[i], 0.005)) return false;
    }
    return true;
  };
  // warehouse doorways (a lintel over a gap): nothing may intrude
  const doorways: AABB[] = [];
  for (const b of boxes) {
    if (Math.abs(b.s[0] - 2) < 0.01 && Math.abs(b.s[2] - 0.4) < 0.01 && b.s[1] > 2 && b.p[1] - b.s[1] / 2 > 2) {
      const y0 = b.p[1] - b.s[1] / 2;
      doorways.push({ x0: b.p[0] - 1, x1: b.p[0] + 1, y0: -1, y1: y0, z0: b.p[2] - 0.7, z1: b.p[2] + 0.7 });
    }
  }
  const inDoor = (r: AABB): boolean => doorways.some((d) => overlaps(r, d, 0.001));

  const push = (p: Partial<ExtrasPiece> & Pick<ExtrasPiece, "mat" | "zone" | "shape" | "x" | "y" | "z" | "sx" | "sy" | "sz">): void => {
    out.push({ nx: 0, ny: 0, nz: 0, color: 0xffffff, host: -1, ...p });
  };

  /** Place a box (or rib / quad / cyl) on a face: tangent centre t, vertical
   *  centre y, tangent width w, height hh, `proud` from the face plane (the
   *  box embeds 1 cm so it never shows a seam). False (nothing placed) when
   *  out of the face or anything else is in the way. */
  const onFace = (f: Face, mat: ExtrasMat, t: number, y: number, w: number, hh: number, proud: number, color: number,
    opts: { shape?: "box" | "rib" | "quad" | "cyl"; reg?: Region; grad?: boolean } = {}): boolean => {
    if (t - w / 2 < f.t0 - 1e-6 || t + w / 2 > f.t1 + 1e-6 || y - hh / 2 < f.y0 - 1e-6 || y + hh / 2 > f.y1 + 1e-6) return false;
    proud = Math.min(proud, WALL_PROUD);
    const shape = opts.shape ?? "box";
    const nC = f.plane + f.sign * (proud / 2 - 0.005);
    const nS = proud + 0.01;
    const cx = f.axis === "x" ? nC : t, cz = f.axis === "z" ? nC : t;
    const sx = f.axis === "x" ? nS : w, sz = f.axis === "z" ? nS : w;
    const r: AABB = { x0: cx - sx / 2, x1: cx + sx / 2, y0: y - hh / 2, y1: y + hh / 2, z0: cz - sz / 2, z1: cz + sz / 2 };
    if (!clear(r, f.host) || inDoor(r)) return false;
    const grad = opts.grad ? { gy: f.y0, gh: f.y1 - f.y0 } : {};
    if (shape === "quad") {
      const q = f.plane + f.sign * proud;
      push({
        mat, zone: "skin", shape, host: f.host, color, reg: opts.reg,
        x: f.axis === "x" ? q : t, y, z: f.axis === "z" ? q : t,
        sx: f.axis === "x" ? 0 : w, sy: hh, sz: f.axis === "z" ? 0 : w,
        nx: f.axis === "x" ? f.sign : 0, nz: f.axis === "z" ? f.sign : 0,
      });
    } else {
      push({
        mat, zone: "skin", shape, host: f.host, color, x: cx, y, z: cz, sx, sy: hh, sz,
        nx: f.axis === "x" ? f.sign : 0, nz: f.axis === "z" ? f.sign : 0, ...grad,
      });
    }
    return true;
  };
  /** the four side faces of box i, each clipped below anything resting on it */
  const facesOf = (i: number): Face[] => {
    const a = A[i];
    const fs: Face[] = [
      { host: i, axis: "x", plane: a.x1, sign: 1, t0: a.z0, t1: a.z1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "x", plane: a.x0, sign: -1, t0: a.z0, t1: a.z1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "z", plane: a.z1, sign: 1, t0: a.x0, t1: a.x1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "z", plane: a.z0, sign: -1, t0: a.x0, t1: a.x1, y0: a.y0, y1: a.y1 },
    ];
    for (const f of fs) {
      const strip: AABB = f.axis === "x"
        ? { x0: Math.min(f.plane - f.sign * 0.02, f.plane + f.sign * 0.3), x1: Math.max(f.plane - f.sign * 0.02, f.plane + f.sign * 0.3), y0: f.y0, y1: f.y1, z0: f.t0, z1: f.t1 }
        : { z0: Math.min(f.plane - f.sign * 0.02, f.plane + f.sign * 0.3), z1: Math.max(f.plane - f.sign * 0.02, f.plane + f.sign * 0.3), y0: f.y0, y1: f.y1, x0: f.t0, x1: f.t1 };
      for (let j = 0; j < A.length; j++) {
        if (j === i || floors.has(j) || !overlaps(strip, A[j], 0.005)) continue;
        if (A[j].y0 > f.y0 + 0.5) f.y1 = Math.min(f.y1, A[j].y0);
      }
    }
    return fs;
  };

  // ---- 1. ISO CONTAINERS -------------------------------------------------
  // 6 × 2.6 × 2.5 boxes. Real 20' boxes: corrugated side + end walls (crest
  // pitch ≈ 0.28 m), four corner posts with castings, top + bottom side rails,
  // double doors on one end with four locking bars, owner code + logo.
  const containers: number[] = [];
  for (let i = 0; i < boxes.length; i++) {
    const s = boxes[i].s;
    if (Math.abs(s[1] - 2.6) < 0.01 && ((Math.abs(s[0] - 6) < 0.01 && Math.abs(s[2] - 2.5) < 0.01) || (Math.abs(s[2] - 6) < 0.01 && Math.abs(s[0] - 2.5) < 0.01))) containers.push(i);
  }
  const PITCH = lowSpec ? 0.56 : 0.28;
  containers.forEach((ci, k) => {
    const b = boxes[ci], a = A[ci];
    const base = hexOf(b.color, 0x888888);
    const longX = b.s[0] > b.s[2];
    const faces = facesOf(ci);
    const doorSign = h01(k, ci, 1) < 0.5 ? 1 : -1;
    const logo = (["logo0", "logo1", "logo2"] as const)[Math.floor(h01(k, ci, 2) * 3)];
    const code = (["code0", "code1", "code2", "code3"] as const)[k % 4];
    const rail = shade(base, 0.78);
    for (const f of faces) {
      const isSide = (f.axis === "z") === longX;
      const isDoor = !isSide && f.sign === doorSign;
      const y0 = f.y0, y1 = f.y1, h = y1 - y0;
      const t0 = f.t0, t1 = f.t1, tc = (t0 + t1) / 2;
      // rails: bottom (deep), top (flush with the roof — no ledge)
      onFace(f, "steel", tc, y0 + 0.08, t1 - t0 - 0.36, 0.16, 0.07, rail, { grad: true });
      onFace(f, "steel", tc, y1 - 0.065, t1 - t0 - 0.36, 0.13, 0.06, rail, { grad: true });
      const ry0 = y0 + 0.16, ry1 = y1 - 0.13;
      if (!isDoor) {
        for (let t = t0 + 0.34; t <= t1 - 0.34 + 1e-6; t += PITCH) {
          const j = h01(Math.round(t * 100), ci, 3);
          onFace(f, "steel", t, (ry0 + ry1) / 2, 0.15, ry1 - ry0, 0.045, shade(base, 0.93 + j * 0.1), { shape: "rib", grad: true });
        }
        if (isSide && !lowSpec) {
          // owner logo mid-panel, code top-right (as seen from outside)
          onFace(f, "paint", tc, y0 + h * 0.62, Math.min(3.8, t1 - t0 - 1.2), 0.95, 0.052, 0xffffff, { shape: "quad", reg: logo });
          const right = (f.axis === "x" ? -f.sign : f.sign) as number; // tangent dir of the viewer's right
          onFace(f, "paint", tc + right * ((t1 - t0) / 2 - 0.85), y1 - 0.42, 1.1, 0.11, 0.052, 0xffffff, { shape: "quad", reg: code });
        }
      } else {
        // DOORS: two leaves, header + sill are the rails above
        const half = (t1 - t0 - 0.36) / 2;
        for (const s of [-1, 1]) {
          const lc = tc + s * (half / 2 + 0.01);
          onFace(f, "steel", lc, (ry0 + ry1) / 2, half - 0.02, ry1 - ry0, 0.03, shade(base, 0.96), { grad: true });
          // two locking bars per leaf, with cam keepers + lever handles
          for (const o of [-0.22, 0.22]) {
            const bt = lc + o * half * 1.1;
            onFace(f, "dark", bt, (ry0 + ry1) / 2, 0.045, ry1 - ry0 - 0.1, 0.085, PAL.hardware);
            onFace(f, "dark", bt, ry1 - 0.12, 0.12, 0.1, 0.1, PAL.hardware);
            onFace(f, "dark", bt, ry0 + 0.12, 0.12, 0.1, 0.1, PAL.hardware);
            onFace(f, "dark", bt + s * 0.13, y0 + 1.15, 0.26, 0.05, 0.11, PAL.hardware);
          }
          // hinges on the outer edge
          if (!lowSpec) for (const hy of [0.45, 1.05, 1.65, 2.2]) {
            if (y0 + hy > ry1 - 0.1) continue;
            onFace(f, "dark", tc + s * (half - 0.04), y0 + hy, 0.1, 0.13, 0.06, PAL.hardware);
          }
        }
        if (!lowSpec) {
          const right = (f.axis === "x" ? -f.sign : f.sign) as number;
          onFace(f, "paint", tc + right * half * 0.5, y1 - 0.42, Math.min(1.1, half * 0.9), 0.11, 0.035, 0xffffff, { shape: "quad", reg: code });
        }
      }
    }
    // corner posts + castings: proud 4 cm on both faces, never above the roof
    for (const [cx, sxn] of [[a.x0, -1], [a.x1, 1]] as const) {
      for (const [cz, szn] of [[a.z0, -1], [a.z1, 1]] as const) {
        const px = cx - sxn * 0.06, pz = cz - szn * 0.06; // centre of a 0.2 post poking 0.04 out
        const top = faces[0].y1 < a.y1 || faces[1].y1 < a.y1 || faces[2].y1 < a.y1 || faces[3].y1 < a.y1
          ? Math.min(faces[0].y1, faces[1].y1, faces[2].y1, faces[3].y1) : a.y1;
        const pr: AABB = { x0: px - 0.1, x1: px + 0.1, z0: pz - 0.1, z1: pz + 0.1, y0: a.y0, y1: top };
        if (!clear(pr, ci)) continue;
        push({ mat: "steel", zone: "skin", shape: "box", host: ci, color: rail, x: px, y: (a.y0 + top) / 2, z: pz, sx: 0.2, sy: top - a.y0, sz: 0.2, gy: a.y0, gh: a.y1 - a.y0 });
        for (const cy of [a.y0 + 0.07, top - 0.07]) {
          push({ mat: "dark", zone: "skin", shape: "box", host: ci, color: PAL.rail, x: cx - sxn * 0.08, y: cy, z: cz - szn * 0.08, sx: 0.26, sy: 0.14, sz: 0.26 });
        }
      }
    }
  });

  // ---- 2. PALLETISED COVER -----------------------------------------------
  // the 1 m cover blocks become wrapped loads on a timber pallet + straps
  for (let i = 0; i < boxes.length; i++) {
    const s = boxes[i].s, a = A[i];
    if (Math.abs(s[1] - 1) > 0.01 || a.y0 > 0.05 || a.y0 < -0.05 || Math.max(s[0], s[2]) > 3.5 || Math.min(s[0], s[2]) < 1.5) continue;
    for (const f of facesOf(i)) {
      const tc = (f.t0 + f.t1) / 2, L = f.t1 - f.t0;
      onFace(f, "dark", tc, a.y0 + 0.07, L, 0.1, 0.008, PAL.shadow);
      onFace(f, "wood", tc, a.y0 + 0.13, L, 0.035, 0.035, PAL.wood, { grad: true });
      onFace(f, "wood", tc, a.y0 + 0.012, L, 0.024, 0.03, PAL.woodDark, { grad: true });
      for (const u of [-0.5, 0, 0.5]) onFace(f, "wood", tc + u * (L - 0.12), a.y0 + 0.07, 0.12, 0.09, 0.03, PAL.woodDark, { grad: true });
      // straps: two vertical bands per long face, carried over the top
      if (L > 2.5) for (const u of [-0.25, 0.25]) onFace(f, "dark", tc + u * L, (a.y0 + 0.15 + a.y1) / 2, 0.035, a.y1 - a.y0 - 0.15, 0.008, PAL.strap);
    }
    const longX = s[0] > s[2];
    for (const u of [-0.25, 0.25]) {
      push({
        mat: "dark", zone: "top", shape: "box", host: i, color: PAL.strap,
        x: longX ? boxes[i].p[0] + u * s[0] : boxes[i].p[0], y: a.y1 + 0.003, z: longX ? boxes[i].p[2] : boxes[i].p[2] + u * s[2],
        sx: longX ? 0.035 : s[0], sy: 0.006, sz: longX ? s[2] : 0.035,
      });
    }
  }

  // ---- 3. WAREHOUSES -----------------------------------------------------
  const roofs: number[] = [];
  for (let i = 0; i < boxes.length; i++) {
    const s = boxes[i].s;
    if (Math.abs(s[0] - 18) < 0.01 && Math.abs(s[2] - 20) < 0.01 && Math.abs(s[1] - 0.3) < 0.01 && A[i].y0 > 5) roofs.push(i);
  }
  /** the wall face (a host box) containing a point just inside face `axis/plane` */
  const hostAt = (x: number, y: number, z: number): number => {
    for (let i = 0; i < A.length; i++) {
      if (floors.has(i)) continue;
      const a = A[i];
      if (x > a.x0 - 0.01 && x < a.x1 + 0.01 && y > a.y0 && y < a.y1 && z > a.z0 - 0.01 && z < a.z1 + 0.01) return i;
    }
    return -1;
  };
  /** outward face of the wall host at (x,y,z) along axis/sign */
  const wallFace = (x: number, y: number, z: number, axis: "x" | "z", sign: 1 | -1): Face | null => {
    const h = hostAt(x, y, z);
    if (h < 0) return null;
    return facesOf(h).find((f) => f.axis === axis && f.sign === sign) ?? null;
  };
  roofs.forEach((ri, wk) => {
    const r = boxes[ri], cx = r.p[0], cz = r.p[2];
    const x0 = cx - 9, x1 = cx + 9, z0 = cz - 10, z1 = cz + 10, H = A[ri].y0 - 0.0;
    const yardSign: 1 | -1 = cx < 0 ? 1 : -1;
    const yard = wallFace(yardSign > 0 ? x1 : x0, 2, cz, "x", yardSign);
    const alley = wallFace(yardSign > 0 ? x0 : x1, 2, cz, "x", (-yardSign) as 1 | -1);
    const front = wallFace(cx + 4, 2, z0, "z", -1);
    const back = wallFace(cx, 2, z1, "z", 1);
    // ROLLER SHUTTER on the yard wall (tangent runs along z). The signage pass
    // already hangs a shopfront (glazing, awning, fascia) on the CENTRE of this
    // face and house windows start a floor up, so the goods-in shutter sits
    // off-centre toward the front corner and stays under the first-floor sills.
    if (yard) {
      const t = cz + SHUTTER_DZ, W = SHUTTER_W, DH = 3.1;
      onFace(yard, "galv", t, 0.02 + DH / 2, W, DH, 0.05, PAL.galv);
      onFace(yard, "dark", t, 0.07, W, 0.12, 0.08, PAL.hardware);
      for (const s of [-1, 1]) onFace(yard, "steel", t + s * (W / 2 + 0.07), (DH + 0.15) / 2, 0.14, DH + 0.15, 0.12, PAL.pipe, { grad: true });
      onFace(yard, "steel", t, DH + 0.28, W + 0.5, 0.45, 0.26, PAL.hood);
      for (const s of [-1, 1]) {
        // hazard paint on the jamb guards, wall packs flanking the hood
        onFace(yard, "paint", t + s * (W / 2 + 0.07), 0.6, 0.14, 1.2, 0.125, 0xffffff, { shape: "quad", reg: "hazard" });
        onFace(yard, "dark", t + s * (W / 2 + 0.45), DH + 0.2, 0.32, 0.24, 0.2, PAL.hardware);
        onFace(yard, "lamp", t + s * (W / 2 + 0.45), DH + 0.17, 0.24, 0.12, 0.205, PAL.lens, { shape: "quad" });
      }
      // site sign above the shopfront fascia
      onFace(yard, "dark", cz, 5.45, 3.7, 0.95, 0.04, PAL.signBack);
      onFace(yard, "paint", cz, 5.45, 3.6, 0.88, 0.045, 0xffffff, { shape: "quad", reg: wk === 0 ? "signA" : "signB" });
      // forklift warning over a junction box (its conduit drops into the slab)
      const side = t - W / 2;
      onFace(yard, "dark", side - 0.7, 2.0, 0.58, 0.58, 0.02, PAL.signBack);
      onFace(yard, "paint", side - 0.7, 2.0, 0.55, 0.55, 0.025, 0xffffff, { shape: "quad", reg: "forklift" });
      onFace(yard, "dark", side - 0.7, 0.95, 0.5, 0.65, 0.18, 0x5b6168);
      onFace(yard, "steel", side - 0.7, 0.34, 0.06, 0.58, 0.07, PAL.pipe);
    }
    // PEDESTRIAN DOOR on the front: steel frame, lamp, signs, electrical panel
    // The front is three hosts (two wall segments + the lintel): resolve the
    // host per piece.
    if (front) {
      const F = (t: number, y: number, mat: ExtrasMat, w: number, hh: number, proud: number, color: number,
        opts: { shape?: "box" | "quad"; reg?: Region } = {}): void => {
        const f = wallFace(t, y, z0, "z", -1);
        if (f) onFace(f, mat, t, y, w, hh, proud, color, opts);
      };
      for (const s of [-1, 1]) F(cx + s * 1.06, 1.2, "dark", 0.12, 2.4, 0.06, PAL.hardware);
      F(cx, 2.46, "dark", 2.0, 0.12, 0.06, PAL.hardware);
      F(cx, 2.85, "dark", 0.32, 0.24, 0.2, PAL.hardware);
      F(cx, 2.82, "lamp", 0.24, 0.12, 0.205, PAL.lens, { shape: "quad" });
      F(cx + 1.75, 1.6, "dark", 0.42, 0.6, 0.02, PAL.signBack);
      F(cx + 1.75, 1.6, "paint", 0.4, 0.58, 0.025, 0xffffff, { shape: "quad", reg: "noSmoke" });
      F(cx - 2.2, 1.6, "dark", 0.84, 0.44, 0.02, PAL.signBack);
      F(cx - 2.2, 1.6, "paint", 0.82, 0.42, 0.025, 0xffffff, { shape: "quad", reg: "auth" });
      F(cx + 3.5, 1.35, "dark", 0.7, 0.9, 0.2, 0x6a7078);
      F(cx + 3.5, 1.35, "dark", 0.02, 0.86, 0.205, 0x34383d);
      F(cx + 3.5, 3.85, "steel", 0.07, 4.1, 0.08, PAL.pipe);
    }
    // DOWNPIPES at the corners of the front + back faces, with hopper heads
    for (const f of [front, back]) {
      if (!f) continue;
      for (const t of [x0 + 0.4, x1 - 0.4]) {
        onFace(f, "steel", t, (0.15 + H - 0.35) / 2, 0.12, H - 0.5, 0.14, PAL.pipe, { shape: "cyl" });
        onFace(f, "steel", t, H - 0.28, 0.28, 0.26, 0.24, PAL.pipe);
        onFace(f, "steel", t, 0.1, 0.2, 0.12, 0.22, PAL.pipe);
      }
    }
    // LOUVRED EXHAUST VENTS on the alley wall
    if (alley) for (const t of [cz - 5, cz + 5]) {
      onFace(alley, "steel", t, 4.6, 1.3, 1.0, 0.1, PAL.hood);
      onFace(alley, "galv", t, 4.6, 1.15, 0.85, 0.12, PAL.galv);
    }
    // SKYLIGHTS flush on the roof (frame + wired glass)
    const ra = A[ri];
    for (const dz of lowSpec ? [-4, 4] : [-6, 0, 6]) {
      for (const dx of [-3.5, 3.5]) {
        const sx = 1.7, sz = 3.2;
        const x = cx + dx, z = cz + dz;
        push({ mat: "dark", zone: "top", shape: "box", host: ri, color: PAL.hardware, x, y: ra.y1 + 0.02, z, sx, sy: 0.04, sz });
        push({ mat: "glass", zone: "top", shape: "quad", host: ri, color: PAL.glass, x, y: ra.y1 + 0.042, z, sx: sx - 0.16, sy: 0, sz: sz - 0.16, ny: 1 });
      }
    }
  });

  // container-stack bridges: hazard edging on the deck
  for (let i = 0; i < boxes.length; i++) {
    const s = boxes[i].s, a = A[i];
    if (!(Math.abs(s[0] - 6) < 0.01 && Math.abs(s[2] - 2) < 0.01 && Math.abs(s[1] - 0.3) < 0.01)) continue;
    for (const ez of [a.z0 + 0.09, a.z1 - 0.09]) {
      for (let x = a.x0 + 0.5; x < a.x1 - 0.01; x += 1) {
        push({ mat: "paint", zone: "top", shape: "quad", host: i, color: 0xffffff, reg: "hazard", x, y: a.y1 + 0.006, z: ez, sx: 1, sy: 0, sz: 0.14, ny: 1 });
      }
    }
  }

  // ---- 4. PERIMETER WALL: concertina, floodlights, signage ---------------
  const perim: number[] = [];
  for (let i = 0; i < boxes.length; i++) {
    const s = boxes[i].s;
    if (Math.abs(s[1] - 5) < 0.01 && Math.max(s[0], s[2]) > 60 && Math.min(s[0], s[2]) <= 1.01) perim.push(i);
  }
  perim.forEach((wi, k) => {
    const a = A[wi], b = boxes[wi];
    const alongX = b.s[0] > b.s[2];
    const top = a.y1;
    const c = alongX ? b.p[2] : b.p[0];   // centre-line coordinate
    const half = (alongX ? b.s[2] : b.s[0]) / 2;
    const inward: 1 | -1 = c > 0 ? -1 : 1;
    // stop short of the crossing walls so corners don't double up
    const lim0 = (alongX ? a.x0 : a.z0) + 1.0, lim1 = (alongX ? a.x1 : a.z1) - 1.0;
    const P = (t: number, y: number, n: number): [number, number, number] => alongX ? [t, y, c + n] : [c + n, y, t];
    const R = 0.3;
    push({
      mat: "wire", zone: "atop", shape: "coil", host: wi, color: PAL.wire,
      a: P(lim0, top + R + 0.06, 0), b: P(lim1, top + R + 0.06, 0),
      ...(() => { const m = P((lim0 + lim1) / 2, top + R + 0.06, 0); return { x: m[0], y: m[1], z: m[2] }; })(),
      sx: alongX ? lim1 - lim0 : 2 * R, sy: 2 * R, sz: alongX ? 2 * R : lim1 - lim0,
    });
    // posts + two straight strands
    const step = 3;
    const n = Math.floor((lim1 - lim0) / step);
    const t0 = (lim0 + lim1) / 2 - (n * step) / 2;
    for (let j = 0; j <= n; j++) {
      const t = t0 + j * step;
      const m = P(t, top + 0.4, 0);
      push({ mat: "dark", zone: "atop", shape: "box", host: wi, color: PAL.pole, x: m[0], y: m[1], z: m[2], sx: 0.06, sy: 0.8, sz: 0.06 });
    }
    for (const sy of [0.45, 0.78]) {
      for (const sn of [-0.04, 0.04]) {
        const pa = P(t0, top + sy, sn), pb = P(t0 + n * step, top + sy, sn);
        push({ mat: "dark", zone: "atop", shape: "seg", host: wi, color: PAL.wire, a: pa, b: pb, x: (pa[0] + pb[0]) / 2, y: top + sy, z: (pa[2] + pb[2]) / 2, sx: 0.012, sy: 0.012, sz: 0.012 });
      }
    }
    // FLOODLIGHTS: pole on the wall top, head leaning over the inner face
    const nL = lowSpec ? 3 : 4;
    for (let j = 0; j < nL; j++) {
      const t = lim0 + ((j + 0.5) / nL) * (lim1 - lim0) + (h01(k, j, 4) - 0.5) * 2;
      const pm = P(t, top + 1.1, 0);
      push({ mat: "dark", zone: "atop", shape: "cyl", host: wi, color: PAL.pole, x: pm[0], y: pm[1], z: pm[2], sx: 0.12, sy: 2.2, sz: 0.12 });
      const hm = P(t, top + 2.25, inward * 0.22);
      push({ mat: "dark", zone: "atop", shape: "box", host: wi, color: PAL.hardware, x: hm[0], y: hm[1], z: hm[2], sx: alongX ? 0.6 : 0.5, sy: 0.22, sz: alongX ? 0.5 : 0.6 });
      const lm = P(t, top + 2.135, inward * 0.24);
      push({ mat: "lamp", zone: "atop", shape: "quad", host: wi, color: PAL.lens, x: lm[0], y: lm[1], z: lm[2], sx: alongX ? 0.5 : 0.4, sy: 0, sz: alongX ? 0.4 : 0.5, ny: -1 });
    }
    // inner face: warning signs + the floodlight feed conduit
    const inner: Face = alongX
      ? { host: wi, axis: "z", plane: c + inward * half, sign: inward, t0: lim0, t1: lim1, y0: a.y0, y1: top }
      : { host: wi, axis: "x", plane: c + inward * half, sign: inward, t0: lim0, t1: lim1, y0: a.y0, y1: top };
    for (let j = 0; j < (lowSpec ? 2 : 4); j++) {
      const t = lim0 + ((j + 0.25) / 4) * (lim1 - lim0) * (lowSpec ? 2 : 1);
      onFace(inner, "dark", t, 2.1, 1.06, 0.4, 0.02, PAL.signBack);
      onFace(inner, "paint", t, 2.1, 1.02, 0.38, 0.025, 0xffffff, { shape: "quad", reg: "danger" });
    }
  });

  // ---- 5. GROUND MARKINGS ------------------------------------------------
  const decal = (x: number, z: number, sx: number, sz: number, reg: Region, lift = 0.008): void => {
    const ft = floorTop(x, z);
    if (!ft) return;
    // every corner must sit on the same floor height (no paint over a step)
    for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const c = floorTop(x + dx * sx / 2, z + dz * sz / 2);
      if (!c || Math.abs(c.top - ft.top) > 1e-6) return;
    }
    const r: AABB = { x0: x - sx / 2, x1: x + sx / 2, z0: z - sz / 2, z1: z + sz / 2, y0: ft.top - 0.1, y1: ft.top + 2.5 };
    if (!clear(r, -1)) return;
    push({ mat: "paint", zone: "ground", shape: "quad", host: ft.host, color: 0xffffff, reg, x, y: ft.top + lift, z, sx, sy: 0, sz, ny: 1 });
  };
  // container bay corner marks (white L's, 0.3 m outside each footprint)
  for (const ci of containers) {
    const a = A[ci];
    if (a.y0 > 0.1) continue;
    for (const [cx, sxn] of [[a.x0 - 0.3, 1], [a.x1 + 0.3, -1]] as const) {
      for (const [cz, szn] of [[a.z0 - 0.3, 1], [a.z1 + 0.3, -1]] as const) {
        decal(cx + sxn * 0.35, cz, 0.7, 0.1, "white");
        decal(cx, cz + szn * 0.35, 0.1, 0.7, "white");
      }
    }
  }
  // hazard hatching in front of each roller shutter (on the lot)
  for (const ri of roofs) {
    const cx = boxes[ri].p[0], cz = boxes[ri].p[2];
    const s = cx < 0 ? 1 : -1;
    const xd = cx + s * 9.2;          // shutter face
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        decal(xd + s * (1.95 + i * 1.1), cz + SHUTTER_DZ - 1.1 + j * 1.1, 1.1, 1.1, "hazard");
      }
    }
  }
  // helipad under the helicopter spawn; parking box for the plane
  for (const v of map.vehicles ?? []) {
    if (v.kind === "heli") decal(v.p[0], v.p[2], 9, 9, "helipad", 0.01);
    else {
      for (const [dx, dz, sx, sz] of [[-4, 0, 0.14, 8], [4, 0, 0.14, 8], [0, -4, 8.14, 0.14], [0, 4, 8.14, 0.14]] as const) decal(v.p[0] + dx, v.p[2] + dz, sx, sz, "yellow");
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// three.js side (browser only)

const tmpM = new THREE.Matrix4();
const tmpQ = new THREE.Quaternion();
const tmpV = new THREE.Vector3();
const tmpA = new THREE.Vector3();
const XAX = new THREE.Vector3(1, 0, 0);
const tmpC = new THREE.Color();
const RUST = new THREE.Color(0x6b4630);

function setColor(geo: THREE.BufferGeometry, p: ExtrasPiece): void {
  tmpC.setHex(p.color);
  const pos = geo.getAttribute("position");
  const n = pos.count;
  const c = new Float32Array(n * 3);
  const r0 = tmpC.r, g0 = tmpC.g, b0 = tmpC.b;
  for (let i = 0; i < n; i++) {
    let r = r0, g = g0, b = b0;
    if (p.gh) {
      // grime + rust creeping up from the bottom, streaks varying along x/z
      const f = Math.max(0, Math.min(1, (pos.getY(i) - p.gy!) / p.gh));
      const streak = h01(Math.round((pos.getX(i) + pos.getZ(i)) * 3), 7, 5);
      const k = Math.pow(1 - f, 4) * (0.35 + streak * 0.4);
      const dk = 0.8 + 0.2 * Math.min(1, f * 4);
      r = (r * (1 - k) + RUST.r * k) * dk; g = (g * (1 - k) + RUST.g * k) * dk; b = (b * (1 - k) + RUST.b * k) * dk;
    }
    c[i * 3] = r; c[i * 3 + 1] = g; c[i * 3 + 2] = b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(c, 3));
}

function quadGeo(p: ExtrasPiece): THREE.BufferGeometry {
  const w = p.nx !== 0 ? p.sz : p.sx;
  const hh = p.ny !== 0 ? p.sz : p.sy;
  const g = new THREE.PlaneGeometry(w, hh);
  if (p.reg) {
    const [x0, y0, x1, y1] = REGIONS[p.reg];
    const M = 1.5; // texel inset against bleeding
    const u0 = (x0 + M) / 1024, u1 = (x1 - M) / 1024, v0 = 1 - (y1 - M) / 1024, v1 = 1 - (y0 + M) / 1024;
    const uv = g.getAttribute("uv");
    for (let i = 0; i < uv.count; i++) uv.setXY(i, u0 + uv.getX(i) * (u1 - u0), v0 + uv.getY(i) * (v1 - v0));
  }
  if (p.ny > 0) g.rotateX(-Math.PI / 2);
  else if (p.ny < 0) g.rotateX(Math.PI / 2);
  else if (p.nx !== 0) g.rotateY(p.nx > 0 ? Math.PI / 2 : -Math.PI / 2);
  else if (p.nz < 0) g.rotateY(Math.PI);
  g.translate(p.x, p.y, p.z);
  return g;
}

function segGeo(p: ExtrasPiece): THREE.BufferGeometry {
  const a = tmpA.set(p.a![0], p.a![1], p.a![2]);
  const d = tmpV.set(p.b![0] - a.x, p.b![1] - a.y, p.b![2] - a.z);
  const len = d.length();
  const g = new THREE.BoxGeometry(len, p.sy, p.sx);
  tmpQ.setFromUnitVectors(XAX, d.normalize());
  tmpM.compose(tmpA.set((p.a![0] + p.b![0]) / 2, (p.a![1] + p.b![1]) / 2, (p.a![2] + p.b![2]) / 2), tmpQ, tmpV.set(1, 1, 1));
  g.applyMatrix4(tmpM);
  return g;
}

/** Corrugation crest: a box whose outer face is narrowed to ~half, so the
 *  sides slope like a real trapezoidal profile and catch light differently. */
function ribGeo(p: ExtrasPiece): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(p.sx, p.sy, p.sz).toNonIndexed();
  const pos = g.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    if (p.nx !== 0 && pos.getX(i) * p.nx > 0) pos.setZ(i, pos.getZ(i) * 0.5);
    else if (p.nz !== 0 && pos.getZ(i) * p.nz > 0) pos.setX(i, pos.getX(i) * 0.5);
  }
  g.deleteAttribute("normal");
  g.computeVertexNormals();
  g.translate(p.x, p.y, p.z);
  return g;
}

/** Concertina razor wire: a helix along a→b whose loops lean along the axis
 *  (the flattened-coil look), as a 3-sided indexed tube. */
function coilGeo(p: ExtrasPiece, lowSpec: boolean): THREE.BufferGeometry {
  const ax = p.b![0] - p.a![0], az = p.b![2] - p.a![2];
  const L = Math.hypot(ax, az);
  const dx = ax / L, dz = az / L;           // axis (horizontal)
  const sx = -dz, sz = dx;                  // side
  const R = p.sy / 2, pitch = lowSpec ? 0.42 : 0.26, per = lowSpec ? 7 : 10, wr = 0.018;
  const loops = Math.floor(L / pitch);
  const N = loops * per + 1;
  const verts = new Float32Array(N * 3 * 3);
  const idx: number[] = [];
  const C = [0, 0, 0], prev = [0, 0, 0];
  const pt = (i: number, o: number[]): void => {
    const th = (i / per) * Math.PI * 2;
    const along = (i / per) * pitch + Math.cos(th) * pitch * 0.9;
    const t = Math.max(0, Math.min(L, along + pitch));
    o[0] = p.a![0] + dx * t + sx * Math.sin(th) * R;
    o[1] = p.a![1] + Math.cos(th) * R;
    o[2] = p.a![2] + dz * t + sz * Math.sin(th) * R;
  };
  for (let i = 0; i < N; i++) {
    pt(i, C); pt(i + 1, prev);
    // tangent
    let tx = prev[0] - C[0], ty = prev[1] - C[1], tz = prev[2] - C[2];
    const tl = Math.hypot(tx, ty, tz) || 1; tx /= tl; ty /= tl; tz /= tl;
    // a normal: radial direction from the axis
    let nx = C[0] - (p.a![0] + dx * ((C[0] - p.a![0]) * dx + (C[2] - p.a![2]) * dz)), ny = C[1] - p.a![1], nz = C[2] - (p.a![2] + dz * ((C[0] - p.a![0]) * dx + (C[2] - p.a![2]) * dz));
    const nl = Math.hypot(nx, ny, nz) || 1; nx /= nl; ny /= nl; nz /= nl;
    const bx = ty * nz - tz * ny, by = tz * nx - tx * nz, bz = tx * ny - ty * nx;
    for (let k = 0; k < 3; k++) {
      const an = (k / 3) * Math.PI * 2, ca = Math.cos(an) * wr, sa = Math.sin(an) * wr;
      verts.set([C[0] + nx * ca + bx * sa, C[1] + ny * ca + by * sa, C[2] + nz * ca + bz * sa], (i * 3 + k) * 3);
    }
    if (i > 0) for (let k = 0; k < 3; k++) {
      const a0 = (i - 1) * 3 + k, a1 = (i - 1) * 3 + ((k + 1) % 3), b0 = i * 3 + k, b1 = i * 3 + ((k + 1) % 3);
      idx.push(a0, b0, a1, a1, b0, b1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g.toNonIndexed();
}

let atlasTex: THREE.CanvasTexture | null = null;
/** The paint atlas: line paint, hazard hatch, signs, container codes + logos,
 *  a helipad. Built once, module-cached. Worn: speckle holes in the alpha. */
function paintAtlas(): THREE.CanvasTexture {
  if (atlasTex) return atlasTex;
  const S = 1024, c = document.createElement("canvas");
  c.width = c.height = S;
  const g = c.getContext("2d")!;
  g.clearRect(0, 0, S, S);
  const rect = (r: readonly number[], fill: string): void => { g.fillStyle = fill; g.fillRect(r[0], r[1], r[2] - r[0], r[3] - r[1]); };
  const clip = (r: readonly number[], fn: () => void): void => {
    g.save(); g.beginPath(); g.rect(r[0], r[1], r[2] - r[0], r[3] - r[1]); g.clip(); fn(); g.restore();
  };
  const text = (r: readonly number[], s: string, font: string, fill: string, dy = 0): void => {
    g.fillStyle = fill; g.font = font; g.textAlign = "center"; g.textBaseline = "middle";
    g.fillText(s, (r[0] + r[2]) / 2, (r[1] + r[3]) / 2 + dy, r[2] - r[0] - 12);
  };
  const R = REGIONS;
  rect(R.white, "#e9e7df");
  rect(R.yellow, "#e0b52a");
  clip(R.hazard, () => {
    rect(R.hazard, "#e7b51c");
    g.fillStyle = "#1c1c1c";
    for (let k = -4; k < 6; k++) {
      const x = R.hazard[0] + k * 32;
      g.beginPath(); g.moveTo(x, R.hazard[1]); g.lineTo(x + 16, R.hazard[1]); g.lineTo(x + 16 + 128, R.hazard[3]); g.lineTo(x + 128, R.hazard[3]); g.fill();
    }
  });
  for (const [r, word] of [[R.signA, "WAREHOUSE A"], [R.signB, "WAREHOUSE B"]] as const) {
    rect(r, "#1f4f8a");
    rect([r[0] + 8, r[1] + 8, r[2] - 8, r[3] - 8], "#f1f1ec");
    rect([r[0] + 8, r[1] + 8, r[0] + 120, r[3] - 8], "#1f4f8a");
    text([r[0] + 8, r[1], r[0] + 120, r[3]], word.slice(-1), "bold 92px Arial, sans-serif", "#ffffff");
    text([r[0] + 120, r[1], r[2], r[1] + 84], "WAREHOUSE", "bold 58px Arial, sans-serif", "#1f2a36", 6);
    text([r[0] + 120, r[1] + 76, r[2], r[3]], "GOODS IN  ·  BAY 1-2", "bold 28px Arial, sans-serif", "#4a5562", -4);
  }
  // no smoking
  rect(R.noSmoke, "#f4f4f0");
  {
    const cx = (R.noSmoke[0] + R.noSmoke[2]) / 2, cy = R.noSmoke[1] + 66;
    g.strokeStyle = "#c8201e"; g.lineWidth = 12;
    g.beginPath(); g.arc(cx, cy, 46, 0, Math.PI * 2); g.stroke();
    g.fillStyle = "#222"; g.fillRect(cx - 30, cy - 5, 50, 10); g.fillStyle = "#d56a2a"; g.fillRect(cx + 20, cy - 5, 10, 10);
    g.beginPath(); g.moveTo(cx - 32, cy - 32); g.lineTo(cx + 32, cy + 32); g.stroke();
    text([R.noSmoke[0], R.noSmoke[1] + 120, R.noSmoke[2], R.noSmoke[3]], "NO SMOKING", "bold 22px Arial, sans-serif", "#c8201e");
  }
  // authorised personnel
  rect(R.auth, "#1d5fa8");
  rect([R.auth[0] + 8, R.auth[1] + 64, R.auth[2] - 8, R.auth[3] - 8], "#f4f4f0");
  text([R.auth[0], R.auth[1], R.auth[2], R.auth[1] + 64], "NOTICE", "bold 46px Arial, sans-serif", "#ffffff", 2);
  text([R.auth[0], R.auth[1] + 64, R.auth[2], R.auth[1] + 128], "AUTHORISED", "bold 44px Arial, sans-serif", "#1d2733");
  text([R.auth[0], R.auth[1] + 120, R.auth[2], R.auth[3]], "PERSONNEL ONLY", "bold 40px Arial, sans-serif", "#1d2733");
  // danger razor wire
  rect(R.danger, "#f4f4f0");
  rect([R.danger[0], R.danger[1], R.danger[2], R.danger[1] + 72], "#c41e1e");
  text([R.danger[0], R.danger[1], R.danger[2], R.danger[1] + 72], "DANGER", "bold 58px Arial, sans-serif", "#ffffff", 3);
  text([R.danger[0], R.danger[1] + 72, R.danger[2], R.danger[3]], "RAZOR WIRE · KEEP OFF", "bold 46px Arial, sans-serif", "#1d2733");
  // forklift warning triangle
  rect(R.forklift, "#f4f4f0");
  {
    const cx = (R.forklift[0] + R.forklift[2]) / 2, top = R.forklift[1] + 10, bot = R.forklift[3] - 14;
    g.fillStyle = "#1a1a1a"; g.beginPath(); g.moveTo(cx, top); g.lineTo(R.forklift[2] - 8, bot); g.lineTo(R.forklift[0] + 8, bot); g.closePath(); g.fill();
    g.fillStyle = "#f0c21c"; g.beginPath(); g.moveTo(cx, top + 16); g.lineTo(R.forklift[2] - 22, bot - 8); g.lineTo(R.forklift[0] + 22, bot - 8); g.closePath(); g.fill();
    g.fillStyle = "#1a1a1a";
    g.fillRect(cx - 26, bot - 46, 34, 22); g.fillRect(cx - 18, bot - 62, 14, 16); g.fillRect(cx + 10, bot - 66, 5, 42); g.fillRect(cx + 10, bot - 28, 20, 5);
    g.beginPath(); g.arc(cx - 20, bot - 20, 7, 0, Math.PI * 2); g.arc(cx + 2, bot - 20, 7, 0, Math.PI * 2); g.fill();
  }
  // container owner codes (ISO 6346 shape: 4 letters, 6 digits, check digit)
  const codes = ["NPXU 482913 7", "KSTU 207731 4", "MRDU 655028 1", "NPXU 319460 2"];
  (["code0", "code1", "code2", "code3"] as const).forEach((k, i) => {
    text(R[k], codes[i], "bold 38px 'Arial Narrow', Arial, sans-serif", "#f2f2ee", 0);
  });
  // owner logos (big stencil words, white on transparent)
  const logos = ["NEONLINE", "KASTOR", "MERIDIAN"];
  (["logo0", "logo1", "logo2"] as const).forEach((k, i) => {
    text(R[k], logos[i], "900 96px Arial Black, Arial, sans-serif", "#f2f2ee", 0);
  });
  // helipad: yellow ring, white H
  {
    const r = R.helipad, cx = (r[0] + r[2]) / 2, cy = (r[1] + r[3]) / 2, rad = (r[2] - r[0]) / 2 - 10;
    g.strokeStyle = "#e3b524"; g.lineWidth = 22; g.beginPath(); g.arc(cx, cy, rad - 11, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = "#ecebe5"; g.lineWidth = 8; g.beginPath(); g.arc(cx, cy, rad - 52, 0, Math.PI * 2); g.stroke();
    g.fillStyle = "#ecebe5";
    g.fillRect(cx - 110, cy - 150, 52, 300); g.fillRect(cx + 58, cy - 150, 52, 300); g.fillRect(cx - 58, cy - 26, 116, 52);
  }
  // WEAR: punch speckle holes through everything painted on the ground/walls
  g.globalCompositeOperation = "destination-out";
  for (let i = 0; i < 9000; i++) {
    const x = h01(i, 1, 11) * S, y = h01(i, 2, 11) * S, rr = 0.6 + h01(i, 3, 11) * 2.2;
    g.globalAlpha = 0.5 + h01(i, 4, 11) * 0.5;
    g.beginPath(); g.arc(x, y, rr, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 1;
  g.globalCompositeOperation = "source-over";
  atlasTex = new THREE.CanvasTexture(c);
  atlasTex.colorSpace = THREE.SRGBColorSpace;
  atlasTex.anisotropy = 4;
  atlasTex.userData.shared = true;
  return atlasTex;
}

/** roller-door / louvre UVs: the galv family's ribs run along V, a roller
 *  shutter's slats are HORIZONTAL — so project with u = height. */
function galvUV(g: THREE.BufferGeometry, tile: number): void {
  const pos = g.getAttribute("position"), uv = g.getAttribute("uv");
  for (let i = 0; i < pos.count; i++) uv.setXY(i, pos.getY(i) / tile, (pos.getX(i) + pos.getZ(i)) / tile);
  uv.needsUpdate = true;
}

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  const pieces = compoundLayout(ctx.map, ctx.lowSpec);
  const groups = new Map<ExtrasMat, THREE.BufferGeometry[]>();
  for (const p of pieces) {
    let g: THREE.BufferGeometry;
    if (p.shape === "quad") g = quadGeo(p);
    else if (p.shape === "seg") g = segGeo(p);
    else if (p.shape === "coil") g = coilGeo(p, ctx.lowSpec);
    else if (p.shape === "rib") g = ribGeo(p);
    else if (p.shape === "cyl") {
      g = new THREE.CylinderGeometry(p.nx || p.nz ? Math.min(p.sx, p.sz) / 2 : p.sx / 2, p.nx || p.nz ? Math.min(p.sx, p.sz) / 2 : p.sx / 2, p.sy, 8, 1, true);
      // a wall-mounted pipe stands off the wall: centre it on the proud band
      g.translate(p.x, p.y, p.z);
    } else {
      g = new THREE.BoxGeometry(Math.max(p.sx, 0.004), Math.max(p.sy, 0.004), Math.max(p.sz, 0.004));
      g.translate(p.x, p.y, p.z);
    }
    if (p.mat === "galv") galvUV(g, 1.2);
    else if (p.mat === "steel" || p.mat === "wood") worldProjectUV(g, p.mat === "wood" ? 1.2 : 2.0);
    if (p.mat !== "paint" && p.mat !== "glass") setColor(g, p);
    if (!g.getAttribute("uv")) g.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(g.getAttribute("position").count * 2), 2));
    const ng = g.index ? g.toNonIndexed() : g;
    if (ng !== g) g.dispose();
    (groups.get(p.mat) ?? groups.set(p.mat, []).get(p.mat)!).push(ng);
  }
  const mats: Record<ExtrasMat, () => THREE.Material> = {
    steel: () => new THREE.MeshStandardMaterial(surfaceMaps("surface", { color: 0xffffff, vertexColors: true, roughness: 0.72, metalness: 0.3 }, 0.5)),
    dark: () => new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.55, metalness: 0.6 }),
    // razor wire: its own mesh so it never enters the shadow pass (a 2 cm wire
    // casts nothing a shadow map resolves; it was ~78k shadow-pass triangles)
    wire: () => new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.55, metalness: 0.6 }),
    galv: () => new THREE.MeshStandardMaterial(surfaceMaps("galv", { color: 0xffffff, vertexColors: true, roughness: 0.6, metalness: 0.5 }, 0.9)),
    wood: () => new THREE.MeshStandardMaterial(surfaceMaps("wood", { color: 0xffffff, vertexColors: true, roughness: 0.92, metalness: 0 }, 0.7)),
    paint: () => new THREE.MeshStandardMaterial({
      map: paintAtlas(), color: 0xffffff, roughness: 0.82, metalness: 0, alphaTest: 0.5,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    }),
    lamp: () => new THREE.MeshBasicMaterial({ color: 0xfff3d6, toneMapped: false, side: THREE.DoubleSide }),
    glass: () => new THREE.MeshStandardMaterial({ color: 0x33404a, roughness: 0.22, metalness: 0.25, envMapIntensity: 0.8, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 }),
  };
  const root = new THREE.Group();
  root.name = "extras:compound";
  for (const [mat, geos] of groups) {
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    if (!merged) { console.error(`[compound extras] merge failed for ${mat}`); continue; }
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, mats[mat]());
    mesh.name = `co:${mat}`;
    mesh.castShadow = ctx.shadows && (mat === "steel" || mat === "dark" || mat === "galv" || mat === "wood");
    mesh.receiveShadow = mat !== "lamp";
    if (mat === "paint") mesh.renderOrder = 1;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    root.add(mesh);
  }
  return root;
}
